import { useState } from "react";
import api from "@/lib/api";

const DOT_GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3Ccircle cx='18' cy='18' r='1' fill='rgba(90,144,168,0.10)'/%3E%3C/svg%3E\")";

const fieldBase = (focused) => ({
  display: "block",
  width: "100%",
  padding: "13px 16px",
  border: `1px solid ${focused ? "#5A90A8" : "rgba(90,144,168,0.25)"}`,
  borderRadius: 10,
  fontSize: 14,
  fontFamily: "Inter, sans-serif",
  color: "#1C3545",
  background: "#F5FAFC",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 200ms ease, box-shadow 200ms ease",
  boxShadow: focused ? "0 0 0 3px rgba(90,144,168,0.12)" : "none",
  paddingRight: 44,
});

const EyeOpen = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export default function ResetPasswordPage({ token, onLoginClick }) {
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [showPw, setShowPw]       = useState(false);
  const [showCf, setShowCf]       = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [cfFocused, setCfFocused] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState("");

  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = !loading && password.length >= 8 && password === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      window.history.replaceState({}, "", window.location.pathname);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        background: "#EDF4F7",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 24px",
        overflow: "hidden",
      }}
    >
      <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: DOT_GRID, backgroundRepeat: "repeat", pointerEvents: "none", zIndex: 1 }} />

      <div
        style={{
          position: "relative",
          zIndex: 2,
          background: "#FFFFFF",
          border: "1px solid rgba(90,144,168,0.18)",
          borderRadius: 22,
          padding: "40px 36px 36px",
          width: "100%",
          maxWidth: 440,
          boxShadow: "0 4px 32px rgba(90,144,168,0.12)",
        }}
      >
        {/* Brand */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <span className="font-serif-display" style={{ fontSize: 19, color: "#1C3545", letterSpacing: "-0.035em" }}>
            Supercore Longevity
          </span>
          <div aria-hidden style={{ width: 28, height: 2, background: "#8AAEBB", borderRadius: 99, margin: "10px auto 0", opacity: 0.8 }} />
        </div>

        {done ? (
          /* ── Success state ── */
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 26 }}>
              ✓
            </div>
            <h1 style={{ margin: "0 0 10px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>
              Password updated
            </h1>
            <p style={{ margin: "0 0 28px", fontSize: 14, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.65 }}>
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <button
              type="button"
              onClick={onLoginClick}
              style={{
                display: "block", width: "100%", padding: "13px",
                background: "#5A90A8", color: "#FFFFFF",
                border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
                fontFamily: "Inter, sans-serif", cursor: "pointer", letterSpacing: "0.01em",
                transition: "background 200ms ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#4D7A8E")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#5A90A8")}
            >
              Sign in
            </button>
          </div>
        ) : (
          /* ── Form state ── */
          <>
            <h1 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>
              Set new password
            </h1>
            <p style={{ margin: "0 0 28px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
              Must be at least 8 characters with a letter and a number.
            </p>

            {error && (
              <div style={{ marginBottom: 18, padding: "11px 14px", borderRadius: 9, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif" }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* New password */}
              <div style={{ marginBottom: 18 }}>
                <label htmlFor="rp-password" style={{ display: "block", fontSize: 13, fontWeight: 500, color: "#4D7A8E", marginBottom: 8, fontFamily: "Inter, sans-serif" }}>
                  New password
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    id="rp-password"
                    type={showPw ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPwFocused(true)}
                    onBlur={() => setPwFocused(false)}
                    style={fieldBase(pwFocused)}
                    disabled={loading}
                  />
                  <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide" : "Show"}
                    style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: showPw ? "#5A90A8" : "#9BBCCC", padding: 0, display: "flex", alignItems: "center", transition: "color 150ms ease" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#5A90A8")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = showPw ? "#5A90A8" : "#9BBCCC")}>
                    {showPw ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div style={{ marginBottom: 24 }}>
                <label htmlFor="rp-confirm" style={{ display: "block", fontSize: 13, fontWeight: 500, color: mismatch ? "#DC2626" : "#4D7A8E", marginBottom: 8, fontFamily: "Inter, sans-serif" }}>
                  Confirm password {mismatch && "— passwords don't match"}
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    id="rp-confirm"
                    type={showCf ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    onFocus={() => setCfFocused(true)}
                    onBlur={() => setCfFocused(false)}
                    style={{ ...fieldBase(cfFocused), borderColor: mismatch ? "rgba(220,38,38,0.5)" : cfFocused ? "#5A90A8" : "rgba(90,144,168,0.25)" }}
                    disabled={loading}
                  />
                  <button type="button" onClick={() => setShowCf((v) => !v)} aria-label={showCf ? "Hide" : "Show"}
                    style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: showCf ? "#5A90A8" : "#9BBCCC", padding: 0, display: "flex", alignItems: "center", transition: "color 150ms ease" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#5A90A8")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = showCf ? "#5A90A8" : "#9BBCCC")}>
                    {showCf ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  display: "block", width: "100%", padding: "13px",
                  background: canSubmit ? "#5A90A8" : "rgba(90,144,168,0.25)",
                  color: canSubmit ? "#FFFFFF" : "rgba(90,144,168,0.5)",
                  border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
                  fontFamily: "Inter, sans-serif", cursor: canSubmit ? "pointer" : "not-allowed",
                  letterSpacing: "0.01em", transition: "background 200ms ease",
                }}
                onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.background = "#4D7A8E"; }}
                onMouseLeave={(e) => { if (canSubmit) e.currentTarget.style.background = "#5A90A8"; }}
              >
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
